import { AppText } from "@toothfairy/shared-ui";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import ToothFairyButton from "../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../Components/ToothFairyComponents/ToothFairyModal";
import { useHistory } from "../../../Router";
import BillingDetailsContainer from "../../Account/Components/BillingDetailsContainer";
import WorkspaceForm from "./WorkspaceForm";
import WorkspaceCreationWizard from "./WorkspaceCreationWizard";
import { ENABLE_CONSUMPTION_BILLING } from "../../../Constants/billing";

const WorkspaceModalContent = React.memo(
  forwardRef(
    (
      {
        modalContentKey,
        handleFormSubmit,
        formValidationSchema,
        initialValues,
        setShareInput,
        setModalContentKey,
        setIsModalOpen,
        handleWorkspaceDeletion,
        cancelPlanChange,
        targetPlan,
        setTargetPlan,
        selectedWorkspace,
        workspaceMutationInProgress,
        onPlanSelection,
        onValuesChange,
        onSubmit,
        forceEnabled,
        onLogoChange,
        workspaceLogo,
        setWorkspaceLogo,
        fileUploadInProgress,
        setFileUploadInProgress,
        fileUploadProgress,
        setFileUploadProgress,
        setWorkspaceLogoFilename,
        eligibleParentWorkspaces,
        hasExistingWorkspaces = false,
        childWorkspacesCount = 0,
      },
      ref,
    ) => {
      const history = useHistory();
      const { t } = useTranslation();
      const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
      const [allowTrial, setAllowTrial] = useState(false);
      const { user } = AppUser.useUser();

      useEffect(() => {
        setHasPaymentMethod(
          user?.stripeCustomerData?.invoice_settings?.default_payment_method !==
            null,
        );
        setAllowTrial(user?.stripeCustomerData?.allowTrial);
      }, [user?.stripeCustomerData]);

      if (modalContentKey === "NEW") {
        if (ENABLE_CONSUMPTION_BILLING) {
          return (
            <WorkspaceCreationWizard
              handleFormSubmit={handleFormSubmit}
              eligibleParentWorkspaces={eligibleParentWorkspaces}
              hasExistingWorkspaces={hasExistingWorkspaces}
              workspaceMutationInProgress={workspaceMutationInProgress}
            />
          );
        }
        return (
          <WorkspaceForm
            selectedWorkspace={selectedWorkspace}
            hasPaymentMethod={hasPaymentMethod}
            allowTrial={allowTrial}
            formValidationSchema={formValidationSchema}
            initialValues={initialValues}
            handleFormSubmit={handleFormSubmit}
            modalContentKey={modalContentKey}
            setIsModalOpen={setIsModalOpen}
            setModalContentKey={setModalContentKey}
            cancelPlanChange={cancelPlanChange}
            targetPlan={targetPlan}
            setTargetPlan={setTargetPlan}
            workspaceMutationInProgress={workspaceMutationInProgress}
            ref={ref}
            onPlanSelection={onPlanSelection}
            onValuesChange={onValuesChange}
            onSubmit={onSubmit}
            forceEnabled={forceEnabled}
            onLogoChange={onLogoChange}
            workspaceLogo={workspaceLogo}
            setWorkspaceLogo={setWorkspaceLogo}
            fileUploadInProgress={fileUploadInProgress}
            setFileUploadInProgress={setFileUploadInProgress}
            fileUploadProgress={fileUploadProgress}
            setFileUploadProgress={setFileUploadProgress}
            setWorkspaceLogoFilename={setWorkspaceLogoFilename}
            eligibleParentWorkspaces={eligibleParentWorkspaces}
          />
        );
      } else if (modalContentKey === "UPDATE") {
        return (
          <WorkspaceForm
            selectedWorkspace={selectedWorkspace}
            hasPaymentMethod={hasPaymentMethod}
            allowTrial={allowTrial}
            formValidationSchema={formValidationSchema}
            initialValues={initialValues}
            handleFormSubmit={handleFormSubmit}
            modalContentKey={modalContentKey}
            setIsModalOpen={setIsModalOpen}
            setModalContentKey={setModalContentKey}
            cancelPlanChange={cancelPlanChange}
            targetPlan={targetPlan}
            setTargetPlan={setTargetPlan}
            workspaceMutationInProgress={workspaceMutationInProgress}
            ref={ref}
            onPlanSelection={onPlanSelection}
            onValuesChange={onValuesChange}
            onSubmit={onSubmit}
            forceEnabled={forceEnabled}
            onLogoChange={onLogoChange}
            workspaceLogo={workspaceLogo}
            setWorkspaceLogo={setWorkspaceLogo}
            fileUploadInProgress={fileUploadInProgress}
            setFileUploadInProgress={setFileUploadInProgress}
            fileUploadProgress={fileUploadProgress}
            setFileUploadProgress={setFileUploadProgress}
            setWorkspaceLogoFilename={setWorkspaceLogoFilename}
            eligibleParentWorkspaces={eligibleParentWorkspaces}
          />
        );
      } else if (modalContentKey === "DELETE") {
        const hasChildren = childWorkspacesCount > 0;
        const subCaption = hasChildren
          ? t("workspaces.deletion.withChildrenSubCaption", {
              count: childWorkspacesCount,
              defaultValue:
                "This will permanently delete the workspace and its data. This workspace has {{count}} sub-workspace(s) which will also be permanently deleted. This action cannot be undone.",
            })
          : t("workspaces.deletion.subCaption", {
              defaultValue:
                "This will permanently delete the workspace and its data. This action cannot be undone.",
            });
        return (
          <ToothFairyModal
            isModalOpen={true}
            setIsModalOpen={() => setModalContentKey("UPDATE")}
            modalStyle={{ maxWidth: 480, width: "90%", height: "auto" }}
            scrollViewStyle={{ margin: 20, width: "100%" }}
            modalTitle={t("workspaces.deletion.caption", {
              defaultValue: "Delete workspace",
            })}
            isCloseButtonVisible
            closeModal={() => setModalContentKey("UPDATE")}
          >
            <View style={_styles.deleteConfirmContainer}>
              <AppText style={_styles.deleteConfirmText}>{subCaption}</AppText>
              <View style={_styles.deleteConfirmActions}>
                <ToothFairyButton
                  title={t("workspaces.deletion.cancelCaption", {
                    defaultValue: "Cancel",
                  })}
                  onPress={() => setModalContentKey("UPDATE")}
                />
                <ToothFairyButton
                  isDeletion
                  title={t("workspaces.deletion.confirmCaption", {
                    defaultValue: "Delete",
                  })}
                  onPress={() => {
                    handleWorkspaceDeletion();
                    setIsModalOpen(false);
                    history.push("/workspaces");
                  }}
                />
              </View>
            </View>
          </ToothFairyModal>
        );
      } else if (modalContentKey === "BILLING") {
        return <BillingDetailsContainer isWizard />;
      }
    },
  ),
);

const _styles = StyleSheet.create({
  deleteConfirmContainer: {
    padding: 20,
    alignItems: "center",
  },
  deleteConfirmText: {
    textAlign: "center",
    marginBottom: 20,
  },
  deleteConfirmActions: {
    flexDirection: "row",
    justifyContent: "center",
    flexWrap: "wrap",
  },
});

export default WorkspaceModalContent;
